// R-131-FIN-DECISION-C — year-by-year pathway cashflow engine.

import { HOURS_PER_YEAR, KW_PER_MW } from '../projectFinance';
import { riskAdjustedWaccFromPathway, withSyncedRiskPremium } from './riskPremium';
import { blendPhaseEconomics } from './presets/shared';
import type { Pathway, PathwayPhase } from './types';

/**
 * Effective CAPEX for a single phase (R-131-FIN-DECISION-MIX).
 *
 * ADDITIVE: an explicit `capexUsd` always wins (existing behaviour). Only
 * when a phase carries a `sourceMix` AND has no explicit `capexUsd` do we
 * derive the capacity-weighted blended *generation* capex. Shared
 * substation/interconnect stays in `pathway.capex` and rides the S-curve
 * exactly as before — so single-source pathways are byte-identical.
 */
function phaseCapexUsd(phase: PathwayPhase): number {
  if (phase.capexUsd != null) return phase.capexUsd;
  if (phase.sourceMix && phase.sourceMix.length > 0) {
    return blendPhaseEconomics(phase.sourceMix).generationCapexUsd;
  }
  return 0;
}

/**
 * Effective annual MWh for an operation phase (R-131-FIN-DECISION-MIX).
 * Explicit `annualMwh` wins; otherwise a `sourceMix` yields the blended
 * (capacity × CF) energy. Absent both, returns undefined (caller falls back
 * to the pathway-level capacity-factor estimate — unchanged behaviour).
 */
function phaseAnnualMwh(phase: PathwayPhase): number | undefined {
  if (phase.annualMwh != null) return phase.annualMwh;
  if (phase.sourceMix && phase.sourceMix.length > 0) {
    return blendPhaseEconomics(phase.sourceMix).annualMwh;
  }
  return undefined;
}

/** Standard construction spend profile — 15 / 35 / 35 / 15 by quartile. */
export const CONSTRUCTION_S_CURVE_WEIGHTS = [0.15, 0.35, 0.35, 0.15] as const;

export interface PathwayCashflow {
  year: number;
  /** 0-indexed calendar-year offset from `now`. */
  cohortYear: number;
  /** CAPEX outflow (USD, positive = cost). */
  capex: number;
  opex: number;
  revenue: number;
  fuel: number;
  refurbishment: number;
  decommissioning: number;
  residualValue: number;
  netCashFlow: number;
}

export interface PathwayTimeline {
  nowYear: number;
  constructionStartYear: number;
  codYear: number;
  /**
   * Precise construction-end (COD / first-MWh) date as ISO `YYYY-MM-DD`. Carries
   * the MONTH resolution that `codYear` discards, so "time to power" can be
   * computed in months from real dates rather than years×12. Derived from the
   * construction phase start + summed durations (same source as `codYear`).
   */
  codIso: string;
  codCohortYear: number;
  eolYear: number;
  /**
   * EXCLUSIVE end of the operating window: `codYear + ppaTermYears`. The LAST
   * year that books revenue, energy, opex and fuel is `revenueEndYear - 1`
   * (the window is the half-open [COD, COD + term) used by `annualRevenueUsd`,
   * the opex/fuel gate and `discountedEnergyMwh`). Kept in the timeline because
   * `endYear` must still span it.
   */
  revenueEndYear: number;
  decommissioningYear: number;
  endYear: number;
}

/**
 * Whole months from `now` to a pathway's COD (first MWh), derived from real
 * Dates — NOT years×12. Returns 0 for a COD already reached / in the past so the
 * "time to power" figure never goes negative. Use this for the months-to-power
 * display on the lifecycle timeline / comparison surfaces.
 */
export function pathwayMonthsToPower(pathway: Pathway, now: Date): number {
  const { codIso } = derivePathwayTimeline(pathway, now);
  const cod = new Date(`${codIso}T00:00:00Z`);
  if (!Number.isFinite(cod.getTime())) return 0;
  const months =
    (cod.getUTCFullYear() - now.getUTCFullYear()) * 12 +
    (cod.getUTCMonth() - now.getUTCMonth());
  return Math.max(0, months);
}

/** Risk-adjusted discount rate as a fraction (e.g. 0.0825). R-131-FIN-DECISION-E */
export function pathwayDiscountRateFraction(pathway: Pathway): number {
  const { waccPct } = riskAdjustedWaccFromPathway(pathway);
  return waccPct / 100;
}

function utcYear(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00Z`).getUTCFullYear();
}

function addMonthsIso(isoDate: string, months: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

function constructionPhases(pathway: Pathway): PathwayPhase[] {
  return pathway.phases.filter((phase) => phase.kind === 'construction');
}

function firstOperationPhase(pathway: Pathway): PathwayPhase | undefined {
  return pathway.phases.find((phase) => phase.kind === 'operation');
}

/**
 * Total construction CAPEX including contingency and BTM extras (USD).
 *
 * CAPEX SPEND INVARIANT (R-131-FIN-DECISION-C):
 *   `phase.capexUsd` is the ONLY spend basis; `capex.substationUsd` /
 *   `capex.transmissionUpgradeUsd` are DESCRIPTIVE components that must sum
 *   INTO it and are never added again on top. The only additions the engine
 *   makes here are contingency (a % OF `capex.baseUsd`, USD) and
 *   `capex.fuelLogisticsUsd` (USD) — neither of which is part of any phase
 *   capex figure. See `distributeConstructionCapexByYear` for the matching
 *   comment at the spend site.
 */
export function totalConstructionCapexUsd(pathway: Pathway): number {
  const constructionCapex = constructionPhases(pathway).reduce(
    (sum, phase) => sum + phaseCapexUsd(phase),
    0,
  );
  const contingencyUsd = pathway.capex.baseUsd * (pathway.capex.contingencyPct / 100);
  return (
    constructionCapex +
    contingencyUsd +
    (pathway.capex.fuelLogisticsUsd ?? 0)
  );
}

/**
 * Spread construction CAPEX across calendar years using the 15/35/35/15
 * S-curve. Grid substation / tie-line costs ride the same curve because they
 * are already INSIDE the construction phase capex (see invariant below).
 *
 * CAPEX SPEND INVARIANT (R-131-FIN-DECISION-C):
 *   `phase.capexUsd` is the ONLY spend basis; `capex.substationUsd` /
 *   `capex.transmissionUpgradeUsd` are DESCRIPTIVE components that must sum
 *   INTO it. They were previously RE-ADDED here on top of the phase capex,
 *   which double-charged every pathway that carries them: the grid-tariff
 *   pathway spent $165.7M instead of $88.6M at 100 MW (1.87x), and the hybrid
 *   bridge $313.1M instead of $236.0M. Do NOT add them back — if a new preset
 *   needs interconnect cost, put it in its construction `phase.capexUsd` and
 *   mirror the split into `capex.substationUsd` / `capex.transmissionUpgradeUsd`
 *   for reporting only.
 */
export function distributeConstructionCapexByYear(
  pathway: Pathway,
): Map<number, number> {
  const phases = constructionPhases(pathway);
  if (phases.length === 0) return new Map();

  // USD. Σ construction phase capex + contingency(baseUsd) + fuel logistics.
  const spendUsd = totalConstructionCapexUsd(pathway);

  const startDate = phases[0].startDate;
  const constructionMonths = phases.reduce((sum, phase) => sum + phase.durationMonths, 0);
  if (constructionMonths <= 0 || spendUsd <= 0) return new Map();

  const quartileMonths = Math.floor(constructionMonths / 4);
  const remainder = constructionMonths - quartileMonths * 4;
  const quartileLengths = [
    quartileMonths + (remainder > 0 ? 1 : 0),
    quartileMonths + (remainder > 1 ? 1 : 0),
    quartileMonths + (remainder > 2 ? 1 : 0),
    quartileMonths,
  ];

  const byYear = new Map<number, number>();
  let monthOffset = 0;
  for (let q = 0; q < 4; q += 1) {
    const weight = CONSTRUCTION_S_CURVE_WEIGHTS[q];
    const monthsInQuartile = quartileLengths[q];
    const quartileSpend = spendUsd * weight;
    const monthly = monthsInQuartile > 0 ? quartileSpend / monthsInQuartile : 0;
    for (let m = 0; m < monthsInQuartile; m += 1) {
      const phaseDate = addMonthsIso(startDate, monthOffset + m);
      const year = utcYear(phaseDate);
      byYear.set(year, (byYear.get(year) ?? 0) + monthly);
    }
    monthOffset += monthsInQuartile;
  }
  return byYear;
}

export function derivePathwayTimeline(pathway: Pathway, now: Date): PathwayTimeline {
  const nowYear = now.getUTCFullYear();
  const construction = constructionPhases(pathway);
  const constructionStartYear =
    construction.length > 0 ? utcYear(construction[0].startDate) : nowYear;

  const constructionMonths = construction.reduce((sum, phase) => sum + phase.durationMonths, 0);
  const constructionEndIso =
    construction.length > 0
      ? addMonthsIso(construction[0].startDate, constructionMonths)
      : `${nowYear}-01-01`;
  const codYear = utcYear(constructionEndIso);
  const codCohortYear = codYear - nowYear;

  const eolYear = codYear + pathway.lifetime.designLifeYears;
  const revenueEndYear = codYear + pathway.revenue.ppaTermYears;
  const decommissioningYear =
    pathway.lifetime.decommissioningYear ?? eolYear;

  const endYear = Math.max(decommissioningYear, revenueEndYear, eolYear);

  return {
    nowYear,
    constructionStartYear,
    codYear,
    codIso: constructionEndIso,
    codCohortYear,
    eolYear,
    revenueEndYear,
    decommissioningYear,
    endYear,
  };
}

export function nameplateAnnualMwh(pathway: Pathway): number {
  // Sim P2.12-S1 — real climate-derated yield wins ABOVE everything else.
  // When a BoP/EO run supplies a finite, positive annualMwh, it is the NET
  // delivered energy (part-load + ambient derate + offline hours already
  // embedded), so it REPLACES the capacity-factor proxy outright. It must NOT
  // be re-multiplied by availabilityPct downstream (annualRevenueUsd already
  // treats the operation annualMwh as CF-adjusted — no double-count). When
  // absent, the legacy fallback chain below is byte-identical.
  const simMwh = pathway.simYield?.annualMwh;
  if (simMwh != null && Number.isFinite(simMwh) && simMwh > 0) {
    return simMwh;
  }
  const operation = firstOperationPhase(pathway);
  // Explicit annualMwh wins (unchanged); else a sourceMix yields blended
  // energy (R-131-FIN-DECISION-MIX). Both paths short-circuit before the
  // pathway-level capacity-factor fallback so single-source pathways are
  // byte-identical.
  const phaseMwh = operation != null ? phaseAnnualMwh(operation) : undefined;
  if (phaseMwh != null && phaseMwh > 0) {
    return phaseMwh;
  }
  const cfFraction = pathway.revenue.availabilityPct / 100;
  if (cfFraction <= 0) return 0;
  return pathway.capacityMW * HOURS_PER_YEAR * cfFraction;
}

function annualFixedOmUsd(pathway: Pathway): number {
  return pathway.opex.fixedOmPerKwYearUsd * pathway.capacityMW * KW_PER_MW;
}

/**
 * The operating rates a pathway is charged at, per year.
 *
 * C9 — a MIXED-SOURCE phase took its CAPEX and its ENERGY from the source mix
 * but its OPEX, FUEL and demand charge from the pathway-level fields, which are
 * the DOMINANT preset's rates applied to the whole blended capacity. A mix of
 * solar and gas was therefore charged gas fixed-O&M on the solar MW (≈1.9×
 * blended opex on the reference mix) — and, because the mix was cheaper on
 * CAPEX, it looked like a technology choice rather than an accounting error.
 *
 * `blendPhaseEconomics` already computes every component's contribution, so
 * the blend is the answer; the trap is what must NOT be added back:
 *
 *  - **No pathway transmission tariff.** The grid component's blended variable
 *    rate already contains `transmissionTariffPerMwhUsd` (see
 *    `sourceEconomicsFor`), so adding the pathway-level term is the same
 *    double-charge as C3 on the standalone grid preset.
 *  - **No pathway demand charge.** The grid component's blended FIXED O&M is
 *    `demandChargePerKwMonthUsd × 12`, i.e. the demand charge is already in
 *    there.
 *
 * Rates are returned PER MWh (not as annual totals) so escalation and any
 * sampled change in delivered energy keep applying exactly as they do for a
 * single-source pathway. A pathway with no source mix returns its own fields
 * unchanged — single-source pathways are byte-identical.
 */
interface OperatingRates {
  fixedOmUsdPerYear: number;
  variableOmPerMwhUsd: number;
  fuelCostPerMwhUsd: number;
  /** Already inside `fixedOmUsdPerYear` for a blended phase. */
  demandChargeUsdPerYear: number;
}

function resolveOperatingRates(pathway: Pathway): OperatingRates {
  const operation = firstOperationPhase(pathway);
  const mix = operation?.sourceMix;
  if (!mix || mix.length === 0) {
    return {
      fixedOmUsdPerYear: annualFixedOmUsd(pathway),
      variableOmPerMwhUsd:
        pathway.opex.variableOmPerMwhUsd + (pathway.opex.transmissionTariffPerMwhUsd ?? 0),
      fuelCostPerMwhUsd: pathway.opex.fuelCostPerMwhUsd,
      demandChargeUsdPerYear: annualDemandChargeUsd(pathway),
    };
  }

  const blended = blendPhaseEconomics(mix);
  const blendedMwh = blended.annualMwh;
  // With no blended energy there is no basis for a per-MWh rate; fall back to
  // the pathway fields rather than dividing by zero.
  if (!Number.isFinite(blendedMwh) || blendedMwh <= 0) {
    return {
      fixedOmUsdPerYear: blended.annualFixedOmUsd,
      variableOmPerMwhUsd: pathway.opex.variableOmPerMwhUsd,
      fuelCostPerMwhUsd: pathway.opex.fuelCostPerMwhUsd,
      demandChargeUsdPerYear: 0,
    };
  }

  return {
    fixedOmUsdPerYear: blended.annualFixedOmUsd,
    variableOmPerMwhUsd: blended.annualVariableOmUsd / blendedMwh,
    fuelCostPerMwhUsd: blended.annualFuelUsd / blendedMwh,
    demandChargeUsdPerYear: 0,
  };
}

/**
 * Annual grid demand charge (USD).
 *
 * Billed peak demand is the FACILITY LOAD at the meter (kW) — NOT the generation
 * NAMEPLATE (`capacityMW`), which is grossed up above the load to serve a firm
 * 24/7 datacenter and would over-bill the demand charge. Rides
 * `pathway.facilityLoadMW` when present; falls back to `capacityMW` for legacy /
 * blended pathways that carry no separate load (unchanged behaviour there).
 */
function annualDemandChargeUsd(pathway: Pathway): number {
  const rate = pathway.opex.demandChargePerKwMonthUsd ?? 0;
  const billedDemandMW =
    pathway.facilityLoadMW != null && pathway.facilityLoadMW > 0
      ? pathway.facilityLoadMW
      : pathway.capacityMW;
  return rate * billedDemandMW * KW_PER_MW * 12;
}

/**
 * Energy-proportional operating charge for a year (USD).
 *
 * ENERGY-CHARGE INVARIANT (R-131-FIN-DECISION-C):
 *   delivered $/MWh = `opex.variableOmPerMwhUsd` + `opex.transmissionTariffPerMwhUsd`.
 *   The two terms are DISJOINT: `variableOmPerMwhUsd` is the pure energy /
 *   variable-O&M component and must NOT already contain the transmission
 *   tariff. The grid preset used to set `variableOmPerMwhUsd = tariff + energy
 *   charge` ($90) AND `transmissionTariffPerMwhUsd` ($42), so this line charged
 *   $132/MWh instead of $90/MWh.
 *
 * Units: [USD/MWh] × [MWh/yr] = [USD/yr]. ✓
 */
function annualVariableOmUsd(rates: OperatingRates, annualMwh: number): number {
  return rates.variableOmPerMwhUsd * annualMwh;
}

function annualFuelUsd(
  pathway: Pathway,
  rates: OperatingRates,
  annualMwh: number,
  yearsFromCod: number,
): number {
  const cagr = (pathway.opex.fuelPriceCagrPct ?? 0) / 100;
  const baseFuel = rates.fuelCostPerMwhUsd * annualMwh;
  return baseFuel * Math.pow(1 + cagr, Math.max(0, yearsFromCod));
}

function annualRevenueUsd(pathway: Pathway, annualMwh: number, yearsFromCod: number): number {
  if (yearsFromCod < 0 || yearsFromCod >= pathway.revenue.ppaTermYears) return 0;
  const escalator = pathway.revenue.ppaEscalatorPctPerYear / 100;
  const ppa =
    pathway.revenue.ppaPricePerMwhUsd * Math.pow(1 + escalator, yearsFromCod);
  const ancillary = pathway.revenue.ancillaryRevenuePerMwhUsd ?? 0;
  // Operation-phase annualMwh is already CF-adjusted from presets; do not
  // apply availabilityPct again (would double-count vs capacityFactorPct).
  return annualMwh * (ppa + ancillary);
}

/**
 * Build the year-by-year cashflow stream for a pathway.
 *
 * Sign convention: outflow fields (`capex`, `opex`, `fuel`, etc.) are
 * positive magnitudes; `netCashFlow = revenue + residualValue − costs`
 * with working-capital recovery folded into `netCashFlow` at EOL.
 */
export function buildPathwayCashflows(pathway: Pathway, now: Date): PathwayCashflow[] {
  // R-131-FIN-DECISION-E — discount at synced baseWaccPct + riskPremiumBps.
  const syncedPathway = withSyncedRiskPremium(pathway);
  const timeline = derivePathwayTimeline(syncedPathway, now);
  const annualMwh = nameplateAnnualMwh(syncedPathway);
  const rates = resolveOperatingRates(syncedPathway);
  const fixedOm = rates.fixedOmUsdPerYear;
  const demandCharge = rates.demandChargeUsdPerYear;
  const workingCapitalUsd = syncedPathway.capex.workingCapitalUsd ?? 0;

  const constructionByYear = distributeConstructionCapexByYear(syncedPathway);
  const refurbByYear = new Map<number, number>();
  for (const event of syncedPathway.opex.refurbishmentEvents ?? []) {
    const year = timeline.codYear + event.yearFromCod;
    refurbByYear.set(year, (refurbByYear.get(year) ?? 0) + event.usd);
  }

  const flows: PathwayCashflow[] = [];
  for (let year = timeline.constructionStartYear; year <= timeline.endYear; year += 1) {
    const cohortYear = year - timeline.nowYear;
    const yearsFromCod = year - timeline.codYear;

    let capex = constructionByYear.get(year) ?? 0;
    let refurbishment = refurbByYear.get(year) ?? 0;
    let decommissioning = 0;
    let residualValue = 0;

    if (year === timeline.codYear && workingCapitalUsd > 0) {
      capex += workingCapitalUsd;
    }

    if (year === timeline.decommissioningYear) {
      decommissioning = syncedPathway.lifetime.decommissioningUsd;
    }

    if (year === timeline.eolYear) {
      residualValue = syncedPathway.lifetime.residualValueUsd ?? 0;
    }

    let opex = 0;
    let fuel = 0;
    let revenue = 0;

    // OPERATING-HORIZON INVARIANT (R-131-FIN-DECISION-C): cost years ==
    // revenue years == energy years. All three use the SAME half-open window
    // [COD, COD + ppaTermYears) — i.e. `yearsFromCod < ppaTermYears`, matching
    // `annualRevenueUsd` above and `discountedEnergyMwh` in pathwayLcoe.ts.
    // The gate was previously `year <= timeline.revenueEndYear` (= COD + term,
    // INCLUSIVE), which booked opex + fuel for ppaTermYears + 1 years against
    // ppaTermYears of revenue — a final year of pure cost with zero revenue and
    // zero energy in the LCOE denominator (~13% of gas-cc NPV).
    if (yearsFromCod >= 0 && yearsFromCod < syncedPathway.revenue.ppaTermYears) {
      opex = annualVariableOmUsd(rates, annualMwh) + demandCharge + fixedOm;
      fuel = annualFuelUsd(syncedPathway, rates, annualMwh, yearsFromCod);
      revenue = annualRevenueUsd(syncedPathway, annualMwh, yearsFromCod);
    }

    let netCashFlow =
      revenue + residualValue - capex - opex - fuel - refurbishment - decommissioning;

    if (year === timeline.eolYear && workingCapitalUsd > 0) {
      netCashFlow += workingCapitalUsd;
    }

    flows.push({
      year,
      cohortYear,
      capex,
      opex,
      revenue,
      fuel,
      refurbishment,
      decommissioning,
      residualValue,
      netCashFlow,
    });
  }

  return flows;
}
